import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

import { ENABLE_FEATURE_FLAG_OVERRIDE_KEY } from "../models/config";
import { FeatureFlag } from "../models/feature-flag-model";

type FeatureFlagOverride = {
  id: string;
  enabled: boolean;
};

type FeatureFlagGlobalStore = {
  // Server data (from API)
  serverFeatureFlags: FeatureFlag[];
  setServerFeatureFlags: (flags: FeatureFlag[]) => void;

  // User overrides (persisted in localStorage)
  overrides: FeatureFlagOverride[];
  setOverride: (id: string, enabled: boolean) => void;
  removeOverride: (id: string) => void;
  clearOverrides: () => void;

  // Computed values
  getFeatureFlag: (id: string) => FeatureFlag | null;
  isFeatureFlagEnabled: (id: string) => boolean;
  canOverride: boolean;

  // Loading state
  isLoading: boolean;
  setLoading: (loading: boolean) => void;
};

export const useFeatureFlagGlobalStore = create<FeatureFlagGlobalStore>()(
  persist(
    (set, get) => ({
      // Server data
      serverFeatureFlags: [],
      setServerFeatureFlags: (flags) => set({ serverFeatureFlags: flags }),

      // User overrides
      overrides: [],
      setOverride: (id, enabled) => {
        set((state) => ({
          overrides: [...state.overrides.filter((override) => override.id !== id), { id, enabled }],
        }));
      },
      removeOverride: (id) => {
        set((state) => ({
          overrides: state.overrides.filter((override) => override.id !== id),
        }));
      },
      clearOverrides: () => set({ overrides: [] }),

      // Computed values
      getFeatureFlag: (id) => {
        const state = get();
        const serverFlag = state.serverFeatureFlags.find((flag) => flag.id === id);
        if (!serverFlag) return null;

        const override = state.overrides.find((override) => override.id === id);
        const canOverride = state.canOverride;

        if (canOverride && override) {
          return {
            ...serverFlag,
            enabled: override.enabled,
          };
        }

        return serverFlag;
      },

      isFeatureFlagEnabled: (id) => {
        const flag = get().getFeatureFlag(id);
        return flag?.enabled || false;
      },

      get canOverride() {
        const state = get();
        const overrideFlag = state.serverFeatureFlags.find((flag) => flag.id === ENABLE_FEATURE_FLAG_OVERRIDE_KEY);
        return overrideFlag?.enabled || false;
      },

      // Loading state
      isLoading: false,
      setLoading: (loading) => set({ isLoading: loading }),
    }),
    {
      name: "feature-flag-overrides", // Only persist overrides, not server data
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ overrides: state.overrides }), // Only persist overrides
    },
  ),
);
